<template>
  <div class="app-activation-top-up-wrapper">
    <div class="text"><slot name="txtBeforeTopUp"></slot></div>
    <EnterAutoSingleTopUp
      :autoTopUp.sync="autoTopUp"
      :singleTopUp.sync="singleTopUp"
      :selected-top-up-types.sync="selectedTopUpTypes"
      :monthly-value="monthlyValue"
      :possibleSingleTopUpAmounts="possibleSingleTopUpAmounts_"
      :showSingleTopUp="showSingleTopUp"
      :show-auto-top-up="showAutoTopUp"
      :selected-tariff="selectedTariff"
      :displayError="displayOptionError"
    >
      <template v-for="slot in possibleChildSlotsFor('AutoSingle')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </EnterAutoSingleTopUp>
    <div class="" v-show="paymentRequired">
      <div class="container-box">
        <div class="content-limiter">
          <h2 class="text-3xl">Zahlungsdaten</h2>
          <p class="top-up-text">Wähle aus, wie du dein Guthaben aufladen möchtest.<br /></p>
          <EnterPaymentData
            :credit-card-redirect-url="creditCardRedirectUrl"
            :credit-card-frame-address="creditCardFrameAddress"
            :payment.sync="payment_"
            :paymentInvalid.sync="paymentInvalid_"
            :payment-context="paymentContext"
            :process-name="'activation'"
            :credit-card-possible="creditCardPossible"
            ref="bankAccountComponent"
          >
            <template v-for="slot in possibleChildSlotsFor('Payment')" v-slot:[`${slot.child}`]>
              <slot :name="`${slot.parent}`"></slot>
            </template>
          </EnterPaymentData>
        </div>
      </div>
    </div>
    <span class="text-sm required-field-hint">*Pflichtfeld</span>
    <Stepper @prev="back()" @next="save()" :loading-next="loading" :disabledNext="loading" :show-prev="true"> </Stepper>
    </div>
</template>

<script>
import EnterAutoSingleTopUp from './EnterAutoSingleTopUp.vue'
import EnterPaymentData from '../../Payment/EnterPaymentData.vue'
import { validationMixin } from 'vuelidate'
import { mapMutations, mapActions, mapGetters } from 'vuex'
import processSteps from '@/mixins/processSteps'
import slotProxy from '@/mixins/slotProxy'
import Stepper from '@/components-v2/generic/Stepper.vue'
import preFilledObjectInput from '@/mixins/preFilledObjectInput'
import preFilledInput from '@/mixins/preFilledInput'
export const stepName = 'topUp'
export default {
  name: 'EnterTopUpAndPayment',
  data() {
    return {
      paymentInvalid_: false,
      loading: false,
      displayOptionError: false
    }
  },
  mixins: [
    validationMixin,
    processSteps(stepName),
    preFilledObjectInput('payment', 'storedPayment'),
    preFilledObjectInput('autoTopUp', 'storedAutoTopUp'),
    preFilledInput('singleTopUp', 'storedSingleTopUp'),
    preFilledInput('selectedTopUpTypes', 'defaultSelectedTopUpTypes'),
    slotProxy()
  ],
  validations() {
    return {
      autoTopUp: {
        validType: (value, vm) => {
          return this.validAutoTopUp
        }
      },
      paymentInvalid_: {
        validPaymentIfRequired: (value, vm) => {
          if (vm.paymentRequired === false) {
            return true
          }
          return value === false
        }
      }
    }
  },
  computed: {
    // Payment
    // # Config
    ...mapGetters('activation/info', {
      possibleSingleTopUpAmounts: 'singleTopUpAmounts'
    }),
    ...mapGetters('activation/tariff', ['savedTariff']),
    possibleSingleTopUpAmounts_: function() {
      if (this.selectedTariff?.tariffExtra?.type === 'year') {
        const amounts = []
        amounts.default = this.selectedTariff.tariffExtra?.singleTopUp
        amounts.amount = [this.selectedTariff.tariffExtra?.singleTopUp]
        return amounts
      }
      return this.possibleSingleTopUpAmounts
    },
    showAutoTopUp: function() {
      return this.savedTariff?.tariffExtra?.allowAutoTopUp
    },
    showSingleTopUp: function() {
      return this.savedTariff?.tariffExtra?.allowTopUp
    },
    // # Data
    ...mapGetters('activation/topUp', {
      storedAutoTopUp: 'topUp',
      storedSingleTopUp: 'singleTopUpAmount',
      storedSelectedTopUpTypes: 'selectedTopUpTypes'
    }),
    defaultSelectedTopUpTypes: function() {
      // if we have already stored a value, this gets first
      // otherwise, all AVAILABLE!!! types are preselected
      if (this.selectedTariff?.tariffExtra?.type === 'year') {
        return [...(this.showSingleTopUp ? ['single'] : [])]
      }
      if (this.storedSelectedTopUpTypes.length === 0) {
        return ['auto', ...(this.showSingleTopUp ? ['single'] : [])]
      }
      return this.storedSelectedTopUpTypes
    },
    // # Validation
    validAutoTopUp: function() {
      return this.selectedTopUpTypes.includes('auto') === false || this.autoTopUp.type !== null
    },
    singleSelected: function() {
      return this.selectedTopUpTypes.includes('single')
    },
    autoSelected: function() {
      // this sets auto as default value
      if (!this.selectedTopUpTypes.includes('nothing')) {
        return this.selectedTopUpTypes.includes('auto')
      }
      return false
    },
    // Payment
    // # Config
    ...mapGetters('activation/info', ['hasValidatedPPlusToken']),
    creditCardPossible: function() {
      // there is a requirement that credit card should not be possible when a valid pplus token is used and Tariff type == PUA
      return this.hasValidatedPPlusToken === false
    },
    // # Data
    ...mapGetters('activation/payment', {
      paymentContext: 'paymentContext',
      storedPayment: 'payment'
    }),
    // # Validation
    paymentRequired: function() {
      return this.singleSelected || this.autoSelected
    },
    // stuff
    getTopUp() {
      return { ...this.storedAutoTopUp }
    },
    fullNameAccountOwner() {
      return this.paymentContext.firstName + ' ' + this.paymentContext.lastName
    }
  },
  methods: {
    ...mapMutations('activation', ['goToStep']),
    ...mapActions('activation/topUp', ['storeAllTopUpData']),
    ...mapActions('activation/submit', ['activationInitiate']),
    back() {
      this.goToStep(this.previousStep)
    },
    async save() {
      let validIBAN = true
      let validPaymentConfirmation = true
      if (this.selectedTopUpTypes.includes('nothing')) {
        this.displayOptionError = true
        this.loading = false
        return
      }
      if (this.$refs.bankAccountComponent) {
        if (this.$refs.bankAccountComponent.$refs.bankAccountComponent) {
          this.$refs.bankAccountComponent.$refs.bankAccountComponent.markInputAsDirty()
          validIBAN = this.$refs.bankAccountComponent.$refs.bankAccountComponent.$v.inputPaymentConfirmation.$invalid
          validPaymentConfirmation = this.$refs.bankAccountComponent.$refs.bankAccountComponent.$v.inputIban.$invalid
        }
        if (this.$refs.bankAccountComponent._paymentType === 'withoutPayment') {
          this.$refs.bankAccountComponent.noPaymentSelected = true
        }
      }

      this.loading = true

      if ((!validIBAN && !validPaymentConfirmation) || this.selectedTopUpTypes.includes('without')) {
        this.payment.owner = this.fullNameAccountOwner
        await this.storeAllTopUpData({
          selectedTopUpTypes: this.selectedTopUpTypes,
          single: this.singleTopUp,
          auto: this.autoTopUp,
          payment: this.payment,
          // do not change step on payment validation. only if initiate was successful we can securely show summary page
          nextStep: stepName
        })
          .then(() => {
            this.loading = false
            this.initiate()
          })
          .catch(() => {
            this.loading = false
          })
      } else {
        this.loading = false
      }
    },
    initiate() {
      this.loading = true
      this.activationInitiate({
        nextStep: this.nextStep
      }).then(() => {
        this.loading = false
      })
      this.loading = false
    }
  },
  watch: {
    selectedTopUpTypes: {
      handler(newVal) {
        if (!newVal.includes('nothing')) {
          this.displayOptionError = false
        }
      }
    },
    payment_: {
      handler(newVal) {
        if (newVal.paymentConfirmation === true) {
          this.displayConfirmationError = false
        }
      }
    }
  },
  components: { EnterPaymentData, EnterAutoSingleTopUp, Stepper },
  props: {
    creditCardFrameAddress: {
      type: String
    },
    creditCardRedirectUrl: {
      type: String
    },
    monthlyValue: {
      type: Array
    },
    selectedTariff: {
      type: Object
    }
  }
}
</script>
